import { action, CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import * as fns from 'date-fns'
import { WaitingScope } from 'src/components/waiting/waiting.scopes'
import { MainPresenter } from 'src/main/Main.presenter'
import serasaHistoryKeys from './serasa-history.keys'
import SerasaHistoryScope, { HeaderScope } from './serasa-history.scopes'
import serasaHistoryService from './serasa-history.service'

const LOG = Logger.get('SerasaHistoryPresenter')

export class SerasaHistoryPresenter extends CubePresenter<MainPresenter, SerasaHistoryScope> {
    // :: Fields

    private readonly __headerBar: Presenter<HeaderScope, SerasaHistoryPresenter>

    // :: Constructor

    public constructor(app: MainPresenter) {
        super(app, new SerasaHistoryScope())
        this.__headerBar = new Presenter(this, this.scope.header)
    }

    // :: Methods

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new serasaHistoryKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope

        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize()
        }

        if (
            initialization ||
            this.scope.startDate !== keys.startDate ||
            this.scope.endDate !== keys.endDate ||
            this.scope.status !== keys.status ||
            this.scope.endpoint !== keys.endpoint
        ) {
            this.scope.startDate = keys.startDate ?? (fns.subDays(new Date(), 30) as Date)
            this.scope.endDate = keys.endDate ?? new Date()
            this.scope.status = keys.status ?? ''
            this.scope.endpoint = keys.endpoint ?? ''

            await this.handleFind({
                startDate: this.scope.startDate,
                endDate: this.scope.endDate,
                status: this.scope.status,
                endpoint: this.scope.endpoint
            })
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new serasaHistoryKeys(this.app, intent)

        keys.startDate = this.scope.startDate
        keys.endDate = this.scope.endDate
        keys.status = this.scope.status
        keys.endpoint = this.scope.endpoint
    }

    async #initialize() {
        LOG.debug('initialize')
        this.scope.onFind = this.handleFind.bind(this)

        try {
            this.scope.waiting = new WaitingScope()

            await Promise.all([this.loadAvailableStatus(), this.loadAvailableEndpoints()])
        } finally {
            this.scope.waiting = undefined
        }
    }

    private async loadAvailableStatus() {
        try {
            this.scope.availableStatus = await serasaHistoryService.fetchSerasaAvailableStatus()
        } catch (error) {
            LOG.error('loadAvailableStatus', error)
            throw error
        }
    }

    private async loadAvailableEndpoints() {
        try {
            this.scope.availableEndpoints = await serasaHistoryService.fetchSerasaAvailableEndpoints()
        } catch (error) {
            LOG.error('loadAvailableEndpoints', error)
            throw error
        }
    }

    @action()
    private async handleFind(params: { startDate?: Date; endDate?: Date; status?: string; endpoint?: string }) {
        try {
            const { startDate, endDate, status, endpoint } = params

            if (!startDate || !endDate) {
                throw new Error('Período deve ser informado')
            }
            if (fns.isAfter(startDate, endDate)) {
                throw new Error('Data inicial não pode ser posterior à data final')
            }
            this.scope.waiting = new WaitingScope()

            this.scope.startDate = startDate
            this.scope.endDate = endDate
            this.scope.status = status || ''
            this.scope.endpoint = endpoint || ''

            const [historyData, urlHistoryStatistics, statusHistoryStatistics] = await Promise.all([
                serasaHistoryService.fetchSerasaHistoryData(
                    this.scope.startDate,
                    this.scope.endDate,
                    this.scope.status,
                    this.scope.endpoint
                ),
                serasaHistoryService.fetchSerasaHistoryStatistics(
                    this.scope.startDate,
                    this.scope.endDate,
                    this.scope.status,
                    this.scope.endpoint,
                    'url'
                ),
                serasaHistoryService.fetchSerasaHistoryStatistics(
                    this.scope.startDate,
                    this.scope.endDate,
                    this.scope.status,
                    this.scope.endpoint,
                    'status'
                )
            ])
            this.scope.data = historyData
            this.scope.urlStatistics = urlHistoryStatistics
            this.scope.statusStatistics = statusHistoryStatistics
        } catch (caught) {
            LOG.error('handleFind', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }
}

export default SerasaHistoryPresenter
